import { isEmpty } from 'lodash'

/**
 * Pure state transitions for the realtime event filters (checklist, deck,
 * postcall, notifications).
 *
 * Each returns the partial filter update to apply, leaving the caller to decide
 * how to store it — Call Explorer dispatches it into redux, the Trends bar
 * merges it into a draft. Shared so an occurrence filter behaves identically on
 * both surfaces.
 */

/**
 * Select or deselect one event entry.
 *
 * @param {object} filters - current filter state
 * @param {object} option - the toggled node, whose `value` is its context path
 */
export const toggleEventEntry = (filters, option) => {
  const { value: entryContext } = option
  const splitEntry = entryContext.split(' - ')
  const [cid, section] = splitEntry

  const { selected: selectedFilters, includes } = filters[section]
  const isEntrySelected = selectedFilters.some((selected) => selected === entryContext)

  if (isEntrySelected) {
    // search lets us deselect all children when the parent is deselected
    return {
      [section]: {
        includes,
        selected: selectedFilters.filter((selected) => selected !== entryContext),
      },
    }
  }

  const contextsToSelect = [entryContext]
  // more than 3 segments means it's a decklist entry, whose parent comes along
  // so "no response used" resolves against the deck it belongs to
  if (section === 'deck' && splitEntry.length > 4) {
    const [, , category, entry] = splitEntry
    const parentContext = `${cid} - ${section} - ${category} - ${entry}`
    if (!selectedFilters.some((selected) => selected === parentContext)) {
      contextsToSelect.push(parentContext)
    }
  }

  return { [section]: { includes, selected: [...selectedFilters, ...contextsToSelect] } }
}

/** Drop every selection in one event section. */
export const clearEventSection = (filters, section) => ({
  [section]: { includes: filters[section].includes, selected: [] },
})

/** Flip a section between "occurred" and "did not occur". */
export const toggleEventIncludes = (filters, section) => ({
  [section]: { includes: !filters[section].includes, selected: filters[section].selected },
})

const openCategory = (filters, category) => {
  const { value, items = [] } = category

  const newOpenCategories = filters.openCategories.includes(value)
    ? filters.openCategories
    : [...filters.openCategories, value]

  if (isEmpty(items)) {
    return { openCategories: newOpenCategories, openSubcategories: filters.openSubcategories }
  }

  const subcategoriesToOpen = items
    .filter((item) => item.has_sub_items)
    .map((item) => `${value} - ${item.item}`)

  return {
    openCategories: newOpenCategories,
    openSubcategories: [...new Set([...filters.openSubcategories, ...subcategoriesToOpen])],
  }
}

const closeCategory = (filters, category) => {
  const { value } = category
  const updatedCategories = filters.openCategories.filter((categoryContext) => {
    const items = category.items.filter((cat) => cat.value === categoryContext)
    return categoryContext !== value && isEmpty(items)
  })
  const updatedSubcategories = filters.openSubcategories.filter((subCatContext) => {
    const items = category.items.filter((cat) => subCatContext.includes(cat.value))
    return !subCatContext.includes(value) && isEmpty(items)
  })

  return { openCategories: updatedCategories, openSubcategories: updatedSubcategories }
}

/** Expand or collapse one category, and any subcategories it owns. */
export const toggleEventCategory = (filters, selectedCategory) =>
  selectedCategory.expanded
    ? openCategory(filters, selectedCategory)
    : closeCategory(filters, selectedCategory)
